import { useRouter } from 'expo-router';
import { useSQLiteContext } from 'expo-sqlite';
import {
  ArrowDown,
  ArrowUp,
  Check,
  ChevronLeft,
  GripVertical,
  RotateCcw,
} from 'lucide-react-native';
import { useEffect, useRef, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import {
  Gesture,
  GestureDetector,
  GestureHandlerRootView,
} from 'react-native-gesture-handler';
import Animated, {
  runOnJS,
  useAnimatedStyle,
  useSharedValue,
  withSpring,
  withTiming,
} from 'react-native-reanimated';
import { SafeAreaView } from 'react-native-safe-area-context';

import { Fonts, Palette, Radius, Spacing } from '@/constants/theme';
import { now } from '@/db/ids';
import { goBackOrTo } from '@/lib/nav';
import {
  DEFAULT_TOOLBAR_KEYS,
  QUICK_ACTIONS,
  parseToolbarKeys,
} from '@/lib/quick-toolbar';
import { getActiveProperty } from '@/lib/tasks';

/**
 * Manage which actions appear in the My Home quick toolbar and in what
 * order. Up to 7 favorites recommended for a clean strip; we don't enforce
 * a hard cap but warn past 8. Reorder via up/down chevrons — full
 * drag-and-drop would need react-native-draggable-flatlist; arrows give
 * 90% of the UX without the dependency.
 */
const SOFT_LIMIT = 7;

export default function QuickToolbarScreen() {
  const router = useRouter();
  const db = useSQLiteContext();
  const [propertyId, setPropertyId] = useState<string | null>(null);
  const [order, setOrder] = useState<string[]>(DEFAULT_TOOLBAR_KEYS);
  const [savedFlash, setSavedFlash] = useState(false);
  // Shared across all rows so non-dragging rows can shift to preview where
  // the dragged row will land. -1 means "no row being dragged".
  const activeIndex = useSharedValue(-1);
  const projectedIndex = useSharedValue(-1);
  const loadedRef = useRef(false);
  const orderRef = useRef<string[]>(order);
  orderRef.current = order;
  const propertyIdRef = useRef<string | null>(null);

  useEffect(() => {
    (async () => {
      const p = await getActiveProperty(db);
      if (!p) return;
      setPropertyId(p.id);
      propertyIdRef.current = p.id;
      const parsed = parseToolbarKeys(p.quick_toolbar_keys_json);
      setOrder(parsed);
      orderRef.current = parsed;
      loadedRef.current = true;
    })();
  }, [db]);

  const persistOrder = async (next: string[]) => {
    const pid = propertyIdRef.current;
    if (!pid) return;
    try {
      await db.runAsync(
        `UPDATE property SET quick_toolbar_keys_json = ?, updated_at = ? WHERE id = ?`,
        JSON.stringify(next),
        now(),
        pid
      );
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1400);
    } catch (err) {
      console.warn('quick-toolbar save failed', err);
    }
  };

  // Save immediately on every order change — no debounce. Earlier debounced
  // version dropped writes when the user pressed Back inside the 300ms
  // window because the effect-cleanup cancelled the pending save.
  useEffect(() => {
    if (!loadedRef.current) return;
    void persistOrder(order);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order]);

  useEffect(() => {
    return () => {
      if (loadedRef.current) void persistOrder(orderRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selected = new Set(order);
  const remaining = QUICK_ACTIONS.filter((a) => !selected.has(a.key));

  const move = (i: number, delta: number) => {
    const next = order.slice();
    const j = i + delta;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    setOrder(next);
  };

  const moveToTop = (i: number) => {
    if (i <= 0) return;
    setOrder((arr) => {
      const next = arr.slice();
      const [item] = next.splice(i, 1);
      next.unshift(item);
      return next;
    });
  };

  const remove = (key: string) => setOrder((arr) => arr.filter((k) => k !== key));
  const add = (key: string) => setOrder((arr) => [...arr, key]);

  const reset = () => setOrder(DEFAULT_TOOLBAR_KEYS);

  return (
    <SafeAreaView style={styles.container} edges={['top', 'bottom']}>
      <View style={styles.topbar}>
        <Pressable
          onPress={() => goBackOrTo(router, '/(tabs)/settings')}
          style={({ pressed }) => [styles.back, pressed && { opacity: 0.7 }]}
          hitSlop={12}>
          <ChevronLeft size={22} color={Palette.ink} strokeWidth={2} />
        </Pressable>
        <Text style={styles.topbarTitle}>Quick toolbar</Text>
        <Pressable onPress={reset} hitSlop={8} style={({ pressed }) => [styles.resetBtn, pressed && { opacity: 0.7 }]}>
          <RotateCcw size={16} color={Palette.inkSoft} strokeWidth={1.8} />
        </Pressable>
      </View>

      <ScrollView contentContainerStyle={{ paddingBottom: 90 }} showsVerticalScrollIndicator={false}>
        <Text style={styles.h1}>Pick your favorites</Text>
        <Text style={styles.sub}>
          These appear in the toolbar at the top of My Home. Drag a row from the
          grip on the left to reorder (or use the up/down arrows), tap × to remove.
          Edit Home wears the filled badge so it's always easy to spot. We
          recommend ~{SOFT_LIMIT}; more works but scrolls.
        </Text>

        <Text style={styles.section}>YOUR TOOLBAR · {order.length}</Text>
        <GestureHandlerRootView style={styles.card}>
          {order.map((key, i) => {
            const a = QUICK_ACTIONS.find((x) => x.key === key);
            if (!a) return null;
            const isFirst = i === 0;
            const isLast = i === order.length - 1;
            const isPrimary = !!a.primary;
            return (
              <DraggableRow
                key={key}
                index={i}
                total={order.length}
                activeIndex={activeIndex}
                projectedIndex={projectedIndex}
                onReorder={(from, to) => {
                  setOrder((arr) => {
                    const next = arr.slice();
                    const [item] = next.splice(from, 1);
                    next.splice(to, 0, item);
                    return next;
                  });
                }}>
                <View style={[styles.row, i < order.length - 1 && styles.rowBorder]}>
                  <View style={styles.grip}>
                    <GripVertical size={16} color={Palette.inkFaint} strokeWidth={1.8} />
                  </View>
                  <View style={[styles.iconBubble, isPrimary && styles.iconBubbleFirst]}>
                    <a.Icon size={18} color={isPrimary ? Palette.cream : Palette.forest} strokeWidth={1.8} />
                  </View>
                  <Text style={styles.rowLabel}>{a.label}</Text>
                  <View style={{ flex: 1 }} />
                  <Pressable
                    onPress={() => move(i, -1)}
                    disabled={isFirst}
                    style={({ pressed }) => [styles.iconBtn, isFirst && { opacity: 0.25 }, pressed && { opacity: 0.6 }]}
                    hitSlop={6}>
                    <ArrowUp size={14} color={Palette.ink} strokeWidth={2} />
                  </Pressable>
                  <Pressable
                    onPress={() => move(i, 1)}
                    disabled={isLast}
                    style={({ pressed }) => [styles.iconBtn, isLast && { opacity: 0.25 }, pressed && { opacity: 0.6 }]}
                    hitSlop={6}>
                    <ArrowDown size={14} color={Palette.ink} strokeWidth={2} />
                  </Pressable>
                  <Pressable
                    onPress={() => remove(key)}
                    style={({ pressed }) => [styles.removeBtn, pressed && { opacity: 0.7 }]}
                    hitSlop={6}>
                    <Text style={styles.removeBtnText}>×</Text>
                  </Pressable>
                </View>
              </DraggableRow>
            );
          })}
          {order.length === 0 ? (
            <Text style={styles.empty}>
              Nothing in your toolbar — pick something from below.
            </Text>
          ) : null}
        </GestureHandlerRootView>

        {order.length > SOFT_LIMIT ? (
          <Text style={styles.warn}>
            {order.length} actions — toolbar will scroll. {SOFT_LIMIT} or fewer fits cleanly.
          </Text>
        ) : null}

        {remaining.length > 0 ? (
          <>
            <Text style={styles.section}>AVAILABLE</Text>
            <View style={styles.card}>
              {remaining.map((a, i) => (
                <Pressable
                  key={a.key}
                  onPress={() => add(a.key)}
                  style={({ pressed }) => [
                    styles.row,
                    i < remaining.length - 1 && styles.rowBorder,
                    pressed && { backgroundColor: '#EEF3EE' },
                  ]}>
                  <View style={[styles.iconBubble, a.primary && styles.iconBubbleFirst]}>
                    <a.Icon size={18} color={a.primary ? Palette.cream : Palette.forest} strokeWidth={1.8} />
                  </View>
                  <Text style={styles.rowLabel}>{a.label}</Text>
                  <View style={{ flex: 1 }} />
                  <Text style={styles.addLink}>+ Add</Text>
                </Pressable>
              ))}
            </View>
          </>
        ) : null}

        <View style={styles.savedRow}>
          <View style={styles.savedPill}>
            {savedFlash ? <Check size={12} color={Palette.cream} strokeWidth={2.6} /> : null}
            <Text style={styles.savedPillText}>
              {savedFlash ? 'Saved' : 'Auto-saves as you go'}
            </Text>
          </View>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: Palette.sand },
  topbar: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: Spacing.four, paddingTop: Spacing.two, paddingBottom: Spacing.two,
  },
  back: {
    width: 36, height: 36, borderRadius: 18, borderWidth: 1,
    borderColor: Palette.line, backgroundColor: Palette.card,
    alignItems: 'center', justifyContent: 'center',
  },
  topbarTitle: { fontFamily: Fonts.sansBold, fontSize: 14, color: Palette.ink },
  resetBtn: {
    width: 36, height: 36, borderRadius: 18,
    alignItems: 'center', justifyContent: 'center',
  },
  h1: {
    marginHorizontal: Spacing.four, marginTop: Spacing.three,
    fontFamily: Fonts.serif, fontSize: 24, color: Palette.ink, letterSpacing: -0.3,
  },
  sub: {
    marginHorizontal: Spacing.four, marginTop: 4,
    fontSize: 13, lineHeight: 19, color: Palette.inkSoft, fontFamily: Fonts.sansMedium,
  },
  section: {
    marginTop: Spacing.five, marginHorizontal: 22,
    fontSize: 12, fontFamily: Fonts.sansBold, color: Palette.inkFaint,
    letterSpacing: 0.8, marginBottom: 8,
  },
  card: {
    marginHorizontal: 18,
    backgroundColor: Palette.card,
    borderWidth: 1, borderColor: Palette.line,
    borderRadius: Radius.xl,
    paddingHorizontal: 14,
  },
  row: {
    flexDirection: 'row', alignItems: 'center', gap: 10,
    paddingVertical: 11,
  },
  rowBorder: { borderBottomWidth: 1, borderBottomColor: Palette.line },
  iconBubble: {
    width: 34, height: 34, borderRadius: 11,
    backgroundColor: '#EAF0EA',
    alignItems: 'center', justifyContent: 'center',
  },
  iconBubbleFirst: { backgroundColor: Palette.forest },
  rowLabel: {
    fontFamily: Fonts.sansBold, fontSize: 14, color: Palette.ink,
  },
  iconBtn: {
    width: 30, height: 30, borderRadius: 10,
    alignItems: 'center', justifyContent: 'center',
    borderWidth: 1, borderColor: Palette.line,
  },
  removeBtn: {
    width: 28, height: 28, borderRadius: 14,
    alignItems: 'center', justifyContent: 'center',
    marginLeft: 4,
  },
  removeBtnText: {
    fontFamily: Fonts.sansBold, fontSize: 18, color: Palette.clay,
    marginTop: -2,
  },
  addLink: { fontFamily: Fonts.sansBold, fontSize: 13.5, color: Palette.forest },
  empty: {
    paddingVertical: 14, fontFamily: Fonts.sansMedium, fontSize: 13,
    color: Palette.inkFaint, textAlign: 'center',
  },
  warn: {
    marginTop: 8, marginHorizontal: 22,
    fontFamily: Fonts.sansMedium, fontSize: 12.5, color: Palette.honeyDeep,
    lineHeight: 16,
  },
  savedRow: {
    marginTop: Spacing.four, alignItems: 'center',
  },
  savedPill: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    paddingHorizontal: 12, paddingVertical: 7,
    borderRadius: 999,
    backgroundColor: '#DCE7E1',
  },
  savedPillText: {
    fontFamily: Fonts.sansBold, fontSize: 12.5, color: Palette.forest,
    letterSpacing: 0.3,
  },
  grip: {
    width: 22, height: 28,
    alignItems: 'center', justifyContent: 'center',
    marginRight: -4,
  },
});

/**
 * Long-press-then-pan to drag a row vertically. While dragging, the parent's
 * `activeIndex` / `projectedIndex` shared values tell every other row
 * whether it should shift up or down to preview the drop position. On
 * release, asks the parent to splice from → projectedIndex.
 */
const ROW_HEIGHT = 54;

type SV = ReturnType<typeof useSharedValue<number>>;

function DraggableRow({
  index,
  total,
  activeIndex,
  projectedIndex,
  onReorder,
  children,
}: {
  index: number;
  total: number;
  activeIndex: SV;
  projectedIndex: SV;
  onReorder: (from: number, to: number) => void;
  children: React.ReactNode;
}) {
  const isDragging = useSharedValue(0);
  const offsetY = useSharedValue(0);

  const pan = Gesture.Pan()
    .activateAfterLongPress(300)
    .onStart(() => {
      isDragging.value = withTiming(1, { duration: 120 });
      activeIndex.value = index;
      projectedIndex.value = index;
    })
    .onUpdate((e) => {
      offsetY.value = e.translationY;
      const delta = Math.round(e.translationY / ROW_HEIGHT);
      projectedIndex.value = Math.max(0, Math.min(total - 1, index + delta));
    })
    .onEnd(() => {
      const target = projectedIndex.value;
      isDragging.value = withTiming(0, { duration: 160 });
      offsetY.value = withSpring(0, { mass: 0.6, damping: 14 });
      activeIndex.value = -1;
      projectedIndex.value = -1;
      if (target !== index) runOnJS(onReorder)(index, target);
    });

  const animatedStyle = useAnimatedStyle(() => {
    const active = activeIndex.value;
    const target = projectedIndex.value;
    const amDragging = active === index;

    // Shift offset for non-dragging rows so they make room for the drop.
    // When dragging i → t (t > i): rows in (i, t] move UP one slot.
    // When dragging i → t (t < i): rows in [t, i) move DOWN one slot.
    let shiftY = 0;
    if (active !== -1 && !amDragging) {
      if (target > active && index > active && index <= target) shiftY = -ROW_HEIGHT;
      else if (target < active && index < active && index >= target) shiftY = ROW_HEIGHT;
    }

    return {
      transform: [
        { translateY: amDragging ? offsetY.value : withTiming(shiftY, { duration: 180 }) },
        { scale: 1 + isDragging.value * 0.03 },
      ],
      zIndex: isDragging.value > 0 ? 999 : 1,
      shadowColor: '#000',
      shadowOpacity: isDragging.value * 0.22,
      shadowRadius: 12,
      shadowOffset: { width: 0, height: 6 },
      elevation: isDragging.value > 0 ? 8 : 0,
      backgroundColor: isDragging.value > 0 ? Palette.card : 'transparent',
      borderRadius: 12,
    };
  });

  return (
    <GestureDetector gesture={pan}>
      <Animated.View style={animatedStyle}>{children}</Animated.View>
    </GestureDetector>
  );
}
